import { Fragment } from 'react';
import { STEPS } from '../../lib/types';

/**
 * 4-step desktop stepper (Upload → Size & Paper → Cart → Checkout).
 * done = warm-grey circle + ✓, active = black fill + number, upcoming = outlined @45%.
 * COMPLETED steps are back-links (Clarity heatmaps showed users clicking the
 * "Upload" label expecting to return there). The ACTIVE step can carry its own
 * action (Clarity round 2: people on step 0 click "Upload" expecting the file
 * picker — screens opt in via onActiveClick). Upcoming steps stay inert so no
 * one skips forward past validation, EXCEPT any listed in `forward` — the
 * owner asked (2026-07-27) for "Cart" to be clickable once the order has
 * prints in it. Those clicks go through the same onStepClick the screen owns,
 * so the review gate still runs.
 */
export function Stepper({
  step,
  onStepClick,
  onActiveClick,
  activeTitle,
  forward,
}: {
  step: number;
  onStepClick?: (step: number) => void;
  /** Steps AHEAD of the current one that are legitimately reachable. */
  forward?: number[];
  /** Optional action for clicking the CURRENT step (e.g. step 0 opens the file picker). */
  onActiveClick?: () => void;
  /** Tooltip/aria hint for the active-step action ("Add photos"). */
  activeTitle?: string;
}) {
  return (
    <div className="stepper" role="list" aria-label="Order steps">
      {STEPS.map((label, i) => {
        const state = i < step ? 'done' : i === step ? 'active' : 'upcoming';
        const unlocked = state === 'upcoming' && !!forward?.includes(i);
        const canNavigate = (state === 'done' || unlocked) && !!onStepClick;
        const canAct = state === 'active' && !!onActiveClick;
        const inner = (
          <>
            <span className={`step__dot step__dot--${state}`} aria-hidden="true">
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span className={`step__label${state === 'active' ? ' step__label--active' : ''}`}>
              {label}
            </span>
          </>
        );
        return (
          <Fragment key={label}>
            {i > 0 && <span className="step__connector" aria-hidden="true" />}
            {canNavigate ? (
              <button
                type="button"
                className="step step--link"
                onClick={() => onStepClick!(i)}
                aria-label={unlocked ? `Go to ${label}` : `Go back to ${label}`}
                title={unlocked ? `Go to ${label}` : `Back to ${label}`}
              >
                {inner}
              </button>
            ) : canAct ? (
              <button
                type="button"
                className="step step--link"
                onClick={onActiveClick}
                aria-current="step"
                aria-label={activeTitle ?? label}
                title={activeTitle}
              >
                {inner}
              </button>
            ) : (
              <div
                className={`step${state === 'upcoming' ? ' step--upcoming' : ''}`}
                role="listitem"
                aria-current={state === 'active' ? 'step' : undefined}
              >
                {inner}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
